import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Dialog } from '@chilecompra/react-kit/components'
import { REACT_APP_CUSTOMER_SUCCESS_BASE_URL } from '../../config/settings/environment'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'
import { onCloseOnboardingStatusSuccess, onUpdateOnboardingStatusThunk } from './OnboardingDialog.actions'
import {
  Description,
  Dot,
  GridContainer,
  GridItem,
  Image,
  Lane,
  Link,
  Root,
  Slide,
  Subtitle,
  Title
} from './OnboardingDialog.styles'

/**
 * The OnboardingDialog's container.
 */
const OnboardingDialog = () => {
  const dispatch = useDispatch()
  const slideWidthRef = useRef('100%')
  const [slide, setSlide] = useState(1)
  const { isSeller } = useAuthorization()
  const { loading, open } = useSelector(state => state.onboardingDialog)

  const handleClose = () => dispatch(onCloseOnboardingStatusSuccess())
  const handleNext = updatedSlide => () => setSlide(updatedSlide)
  const handleStart = () => dispatch(onUpdateOnboardingStatusThunk())

  useEffect(() => {
    if (!open) return

    setSlide(1)

    const dialogElement = document.getElementById('__portal')?.firstChild?.firstChild || null
    if (!dialogElement) return

    const computedElement = window.getComputedStyle(dialogElement)
    slideWidthRef.current = computedElement.getPropertyValue('width')
  }, [open])

  if (loading || !isSeller) {
    return null
  }

  return (
    <Dialog open={open} maxWidth="680px" verticalAlign="top" onClose={handleClose}>
      <Root>
        <Lane slideNumber={slide} slideWidth={slideWidthRef.current}>
          <Slide>
            <GridContainer padding="0 0 13px">
              <GridItem align="center" xs={12}>
                <Image alt="Tutorial Paso 1" src="/assets/images/onboarding_slide-1.svg" />
              </GridItem>
            </GridContainer>
            <GridContainer padding="0 0 5px">
              <GridItem align="center" xs={12}>
                <Title>Te damos la bienvenida a Compra Ágil</Title>
              </GridItem>
            </GridContainer>
            <GridContainer padding="0 0 19px">
              <GridItem align="center" xs={12}>
                <Subtitle>Cotiza y vende de forma rápida y simple</Subtitle>
              </GridItem>
            </GridContainer>
            <GridContainer padding="0 0 36px">
              <GridItem align="center" xs={12}>
                <Description>
                  Revisa las oportunidades en Compra Ágil publicadas por los organismos públicos en todos los productos
                  y servicios que vendes.
                </Description>
              </GridItem>
            </GridContainer>
            <GridContainer padding="0 0 28px">
              <GridItem align="center" xs={12}>
                <Dot active />
                <Dot />
                <Dot />
              </GridItem>
            </GridContainer>
            <GridContainer padding="0 0 41px">
              <GridItem align="center" xs={12}>
                <Button color="primary" variant="outlined" onClick={handleNext(2)}>
                  Siguiente
                </Button>
              </GridItem>
            </GridContainer>
            <GridContainer padding="0 0 20px">
              <GridItem align="center" xs={12}>
                <Link href={REACT_APP_CUSTOMER_SUCCESS_BASE_URL} rel="noreferrer" target="_blank">
                  Ir al Centro de Ayuda
                </Link>
              </GridItem>
            </GridContainer>
          </Slide>
          <Slide>
            <GridContainer padding="0 0 13px">
              <GridItem align="center" xs={12}>
                <Image alt="Tutorial Paso 2" src="/assets/images/onboarding_slide-2.svg" />
              </GridItem>
            </GridContainer>
            <GridContainer padding="0 0 5px">
              <GridItem align="center" xs={12}>
                <Title>Filtra tus búsquedas</Title>
              </GridItem>
            </GridContainer>
            <GridContainer padding="0 0 19px">
              <GridItem align="center" xs={12}>
                <Subtitle>Cotiza y vende de forma rápida y simple</Subtitle>
              </GridItem>
            </GridContainer>
            <GridContainer padding="0 0 36px">
              <GridItem align="center" xs={12}>
                <Description>
                  Puedes filtrar por región, organismo público o palabra clave, entre otros. Una vez que encuentres una
                  Compra Ágil de tu interés, podrás enviar tus cotizaciones completando el formulario en línea.
                </Description>
              </GridItem>
            </GridContainer>
            <GridContainer padding="0 0 28px">
              <GridItem align="center" xs={12}>
                <Dot />
                <Dot active />
                <Dot />
              </GridItem>
            </GridContainer>
            <GridContainer padding="0 0 41px">
              <GridItem align="center" xs={12}>
                <Button color="primary" variant="outlined" onClick={handleNext(3)}>
                  Siguiente
                </Button>
              </GridItem>
            </GridContainer>
            <GridContainer padding="0 0 20px">
              <GridItem align="center" xs={12}>
                <Link href={REACT_APP_CUSTOMER_SUCCESS_BASE_URL} rel="noreferrer" target="_blank">
                  Ir al Centro de Ayuda
                </Link>
              </GridItem>
            </GridContainer>
          </Slide>
          <Slide>
            <GridContainer padding="0 0 13px">
              <GridItem align="center" xs={12}>
                <Image alt="Tutorial Paso 3" src="/assets/images/onboarding_slide-3.svg" />
              </GridItem>
            </GridContainer>
            <GridContainer padding="0 0 5px">
              <GridItem align="center" xs={12}>
                <Title>Revisa si eres el seleccionado</Title>
              </GridItem>
            </GridContainer>
            <GridContainer padding="0 0 19px">
              <GridItem align="center" xs={12}>
                <Subtitle>Cotiza y vende de forma rápida y simple</Subtitle>
              </GridItem>
            </GridContainer>
            <GridContainer padding="0 0 36px">
              <GridItem align="center" xs={12}>
                <Description>
                  Una vez finalizado cada proceso, te notificaremos si resultas ser seleccionado para que hagas entrega
                  de los productos o servicios que cotizaste.
                </Description>
              </GridItem>
            </GridContainer>
            <GridContainer padding="0 0 28px">
              <GridItem align="center" xs={12}>
                <Dot />
                <Dot />
                <Dot active />
              </GridItem>
            </GridContainer>
            <GridContainer padding="0 0 41px">
              <GridItem align="center" xs={12}>
                <Button color="primary" onClick={handleStart}>
                  Comenzar
                </Button>
              </GridItem>
            </GridContainer>
            <GridContainer padding="0 0 20px">
              <GridItem align="center" xs={12}>
                <Link href={REACT_APP_CUSTOMER_SUCCESS_BASE_URL} rel="noreferrer" target="_blank">
                  Ir al Centro de Ayuda
                </Link>
              </GridItem>
            </GridContainer>
          </Slide>
        </Lane>
      </Root>
    </Dialog>
  )
}

export default OnboardingDialog
